import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { addDays, format, subDays } from "date-fns"
import {
  ArrowDownLeft,
  ArrowUpRight,
  Package,
  RefreshCw,
  Sailboat,
} from "lucide-react"
import { useCallback, useState } from "react"
import { z } from "zod"

import { CalendarService } from "@/client"
import { CalendarDateNavigation } from "@/components/Calendar/CalendarDateNavigation.tsx"
import { CalendarFreshnessIndicator } from "@/components/Calendar/CalendarFreshnessIndicator.tsx"
import { KayakDetailModal } from "@/components/Calendar/KayakDetailModal"
import { KayakInventoryPanel } from "@/components/Calendar/KayakInventoryPanel"
import { KayakRentalCard } from "@/components/Calendar/KayakRentalCard"
import { OfflineBanner } from "@/components/Calendar/OfflineBanner.tsx"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import type { GuestType } from "@/constants/booking"
import { useOnlineStatus } from "@/hooks/useOnlineStatus"
import { usePullToRefresh } from "@/hooks/usePullToRefresh"
import { useSwipeNavigation } from "@/hooks/useSwipeNavigation"
import { parseLocalDate } from "@/lib/utils"

// Types for the kayak calendar response
export interface KayakRentalEntry {
  rental_id: string
  booking_id: string
  guest_name: string
  guest_type: GuestType
  equipment_type: string
  equipment_size: string
  pickup_date: string
  pickup_time: string
  return_date: string
  return_time: string
  notes: string | null
  guest_phone: string | null
  guest_email: string | null
  water_taxi_time: string | null
}

export interface KayakEquipmentInventory {
  equipment_type: string
  equipment_size: string
  label: string
  total: number
  rented: number
  available: number
}

interface KayakDailySummary {
  total_out: number
  available: number
  returning_today: number
  pickups_today: number
}

// Search params schema for URL state
const searchParamsSchema = z.object({
  date: z.string().optional(),
})

export const Route = createFileRoute("/_layout/calendar/kayak")({
  component: KayakCalendarPage,
  validateSearch: searchParamsSchema,
  staticData: {
    pageTitle: "Kayak Rentals",
    pageIcon: Sailboat,
    pageIconStyle: { color: "#F5C518" },
  },
  head: () => ({
    meta: [
      {
        title: "Kayak Calendar - Broken Islands Booking",
      },
    ],
  }),
})

/**
 * Render an empty-state card indicating there are no kayak rentals for the provided date.
 */
function EmptyCalendarState({
  selectedDate,
  onGoToToday,
}: {
  selectedDate: Date
  onGoToToday: () => void
}) {
  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        <Sailboat className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">No rentals for this day</h3>
        <p className="text-muted-foreground mb-4">
          There are no kayak rentals scheduled for{" "}
          {format(selectedDate, "MMMM d, yyyy")}
        </p>
        <Button variant="outline" onClick={onGoToToday}>
          <Sailboat className="mr-2 h-4 w-4" />
          Go to Today
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * Render an error UI for calendar loading failures with a retry action.
 */
function CalendarErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <Card className="border-destructive">
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        <RefreshCw className="h-12 w-12 text-destructive mb-4" />
        <h3 className="text-lg font-medium mb-2">Unable to load calendar</h3>
        <p className="text-muted-foreground mb-4">
          There was a problem loading the kayak calendar.
        </p>
        <Button variant="outline" onClick={onRetry}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Try Again
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * Render a three-card daily summary displaying counts for kayaks out, available, and returning.
 */
function KayakSummary({ summary }: { summary: KayakDailySummary }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
      <Card>
        <CardContent className="p-3 sm:p-4 flex items-center gap-3">
          <div className="bg-amber-500/10 p-2 rounded-lg">
            <Package className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
          </div>
          <div>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Out Today
            </p>
            <p className="text-xl sm:text-2xl font-semibold">
              {summary.total_out}
            </p>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-3 sm:p-4 flex items-center gap-3">
          <div className="bg-green-500/10 p-2 rounded-lg">
            <Sailboat className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
          </div>
          <div>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Available
            </p>
            <p className="text-xl sm:text-2xl font-semibold">
              {summary.available}
            </p>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-3 sm:p-4 flex items-center gap-3">
          <div className="bg-blue-500/10 p-2 rounded-lg">
            <ArrowDownLeft className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
          </div>
          <div>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Returning
            </p>
            <p className="text-xl sm:text-2xl font-semibold">
              {summary.returning_today}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

/**
 * Render a skeleton placeholder for a kayak rental card.
 */
function RentalCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <Skeleton className="h-12 w-12 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Render the Kayak Rentals calendar page.
 */
function KayakCalendarPage() {
  const navigate = useNavigate({ from: Route.fullPath })
  const searchParams = Route.useSearch()
  const [selectedRental, setSelectedRental] = useState<KayakRentalEntry | null>(
    null,
  )
  const { isOnline } = useOnlineStatus()

  // Parse selected date from URL or use today
  const selectedDate = searchParams.date
    ? parseLocalDate(searchParams.date)
    : new Date()

  // Fetch calendar data with polling
  const {
    data: calendarData,
    isLoading,
    isError,
    refetch,
    dataUpdatedAt,
    isRefetching,
  } = useQuery({
    queryKey: ["calendar", "kayak", format(selectedDate, "yyyy-MM-dd")],
    queryFn: () =>
      CalendarService.getKayakCalendar({
        requestedDate: format(selectedDate, "yyyy-MM-dd"),
      }),
    refetchInterval: 2 * 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
  })

  // Navigation handlers
  const handlePrevDay = useCallback(() => {
    const prevDate = subDays(selectedDate, 1)
    navigate({
      to: ".",
      search: { date: format(prevDate, "yyyy-MM-dd") },
    })
  }, [selectedDate, navigate])

  const handleNextDay = useCallback(() => {
    const nextDate = addDays(selectedDate, 1)
    navigate({
      to: ".",
      search: { date: format(nextDate, "yyyy-MM-dd") },
    })
  }, [selectedDate, navigate])

  const handleGoToToday = useCallback(() => {
    navigate({
      to: ".",
      search: {},
    })
  }, [navigate])

  const handleDateSelect = useCallback(
    (date: Date) => {
      navigate({
        to: ".",
        search: { date: format(date, "yyyy-MM-dd") },
      })
    },
    [navigate],
  )

  // Swipe navigation
  const swipeHandlers = useSwipeNavigation({
    onSwipeLeft: handleNextDay,
    onSwipeRight: handlePrevDay,
    minDistance: 50,
  })

  // Pull to refresh
  const pullHandlers = usePullToRefresh({
    onRefresh: async () => {
      await refetch()
    },
    threshold: 60,
    enabled: isOnline,
  })

  // Combined touch handlers
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      pullHandlers.onTouchStart(e)
      swipeHandlers.onTouchStart(e)
    },
    [pullHandlers, swipeHandlers],
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      pullHandlers.onTouchMove(e)
      swipeHandlers.onTouchMove(e)
    },
    [pullHandlers, swipeHandlers],
  )

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      pullHandlers.onTouchEnd(e)
      swipeHandlers.onTouchEnd()
    },
    [pullHandlers, swipeHandlers],
  )

  // Get calendar data safely
  const calendar = calendarData?.data as Record<string, unknown> | undefined
  const pickups = (calendar?.pickups ?? []) as KayakRentalEntry[]
  const returns = (calendar?.returns ?? []) as KayakRentalEntry[]
  const summary = calendar?.summary as KayakDailySummary | undefined
  const inventory = (calendar?.inventory ?? []) as KayakEquipmentInventory[]

  const hasRentals = pickups.length > 0 || returns.length > 0

  return (
    <div
      ref={pullHandlers.containerRef}
      className="container py-6 touch-pan-y overscroll-y-contain"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Offline Banner */}
      {!isOnline && <OfflineBanner className="mb-4" />}

      {/* Pull to refresh indicator */}
      {pullHandlers.isPulling && (
        <div
          className="flex justify-center py-2 transition-transform"
          style={{
            transform: `translateY(${Math.min(pullHandlers.pullDistance, 60)}px)`,
          }}
        >
          <RefreshCw
            className={`h-5 w-5 text-muted-foreground ${
              pullHandlers.pullDistance >= 60 ? "animate-spin" : ""
            }`}
          />
        </div>
      )}

      {/* Detail Modal */}
      <KayakDetailModal
        rental={selectedRental}
        open={!!selectedRental}
        onOpenChange={(open) => {
          if (!open) setSelectedRental(null)
        }}
      />

      {/* Date navigation bar */}
      <CalendarDateNavigation
        selectedDate={selectedDate}
        onPrevDay={handlePrevDay}
        onNextDay={handleNextDay}
        onGoToToday={handleGoToToday}
        onDateSelect={handleDateSelect}
        className="mb-6"
      />

      {/* Content */}
      {isLoading ? (
        <div className="space-y-6">
          <KayakSummary
            summary={{
              total_out: 0,
              available: 0,
              returning_today: 0,
              pickups_today: 0,
            }}
          />
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Pickups Today</h3>
            {Array.from({ length: 2 }).map((_, i) => (
              <RentalCardSkeleton key={`pickup-skel-${i}`} />
            ))}
            <h3 className="text-lg font-medium">Returns Today</h3>
            {Array.from({ length: 2 }).map((_, i) => (
              <RentalCardSkeleton key={`return-skel-${i}`} />
            ))}
          </div>
        </div>
      ) : isError ? (
        <CalendarErrorState onRetry={() => refetch()} />
      ) : !hasRentals ? (
        <EmptyCalendarState
          selectedDate={selectedDate}
          onGoToToday={handleGoToToday}
        />
      ) : (
        <>
          {/* Daily summary */}
          {summary && <KayakSummary summary={summary} />}

          {/* Freshness indicator */}
          {dataUpdatedAt && (
            <div className="mb-4">
              <CalendarFreshnessIndicator
                dataUpdatedAt={dataUpdatedAt}
                onRefresh={() => refetch()}
                isRefetching={isRefetching}
              />
            </div>
          )}

          {/* Pickups section */}
          {pickups.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                <ArrowUpRight className="h-5 w-5 text-green-600" />
                Pickups Today ({pickups.length})
              </h3>
              <div className="space-y-3">
                {pickups.map((rental) => (
                  <KayakRentalCard
                    key={`pickup-${rental.rental_id}`}
                    rental={rental}
                    type="pickup"
                    onClick={() => setSelectedRental(rental)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Returns section */}
          {returns.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                <ArrowDownLeft className="h-5 w-5 text-blue-600" />
                Returns Today ({returns.length})
              </h3>
              <div className="space-y-3">
                {returns.map((rental) => (
                  <KayakRentalCard
                    key={`return-${rental.rental_id}`}
                    rental={rental}
                    type="return"
                    onClick={() => setSelectedRental(rental)}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Inventory panel (always shown when data loaded) */}
      {!isLoading && !isError && inventory.length > 0 && (
        <KayakInventoryPanel inventory={inventory} />
      )}
    </div>
  )
}
